import { useEffect, useState } from 'react'
import { consultationOfflineStore, OfflineConsultation, OfflinePatient, patientOfflineStore } from '@/core/offline'

type UseOfflineConsultationLoaderProps = {
  consultationId?: string
  isOfflineDatabaseReady: boolean
}

type StatusState = {
  error: string | null
  loading: boolean
}

export function useOfflineConsultationLoader({ consultationId, isOfflineDatabaseReady }: UseOfflineConsultationLoaderProps) {
  const [consultation, setConsultation] = useState<OfflineConsultation | null>(null)
  const [patient, setPatient] = useState<OfflinePatient | null>(null)
  const [status, setStatus] = useState<StatusState>({ error: null, loading: true })

  useEffect(() => {
    const loadConsultationData = async () => {
      if (!consultationId || !isOfflineDatabaseReady) return

      try {
        setStatus({ error: null, loading: true })

        // Load consultation from offline store
        const offlineConsultation = await consultationOfflineStore.get(consultationId)
        if (!offlineConsultation) {
          setStatus((prev) => ({ ...prev, error: 'Consultation not found' }))
          return
        }

        setConsultation(offlineConsultation)

        // Load patient data - handle both string and object patient references
        let patientId: string
        if (typeof offlineConsultation.patient === 'string') {
          patientId = offlineConsultation.patient
        } else {
          patientId = offlineConsultation.patient._id
        }

        const offlinePatient = await patientOfflineStore.get(patientId)
        if (offlinePatient) {
          setPatient(offlinePatient)
        }
      } catch (error) {
        console.error('Error loading consultation data:', error)
        setStatus((prev) => ({ ...prev, error: 'Failed to load consultation data' }))
      } finally {
        setStatus((prev) => ({ ...prev, loading: false }))
      }
    }

    loadConsultationData()
  }, [consultationId, isOfflineDatabaseReady])

  return {
    consultation,
    patient,
    error: status.error,
    loading: status.loading,
    setConsultation,
  }
}
